import { useEffect, useState } from "react";

import {
  APIError,
  deviceLabel,
  getGlobalDeviceExtensions,
  setGlobalDeviceExtension,
  type Device,
  type DeviceExtensions as ExtensionState,
} from "./api";

// Warnings of the Pi extension manager; the extension keeps running.
const extensionCodeTexts: Record<string, string> = {
  update_download_failed: "Update-Download fehlgeschlagen · installierte Version läuft weiter, neuer Versuch automatisch",
  slot_integrity_failed: "Installierte Dateien weichen ab · läuft weiter, Reparatur beim nächsten Start",
};

function extensionRequestPending(extension: ExtensionState["extensions"][number]) {
  const failed = Boolean(extension.error_code) || extension.state === "failed" || extension.state === "error";
  if (failed) return false;
  if (!extension.enabled) return extension.reported_enabled === true || extension.state === "stopping";
  return Boolean(extension.desired_version) && extension.desired_version === extension.latest_version &&
    (extension.installed_version !== extension.desired_version || extension.reported_enabled === false);
}

export function DeviceExtensions({ device, embedded = false, onClose, onError, onNotice }: {
  device: Device;
  embedded?: boolean;
  onClose: () => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [state, setState] = useState<ExtensionState | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [changing, setChanging] = useState("");

  useEffect(() => {
    let active = true;
    getGlobalDeviceExtensions(device.id)
      .then((loaded) => { if (active) { setState(loaded); setLoadFailed(false); } })
      .catch(() => { if (active) { setLoadFailed(true); onError("Die Extensions konnten nicht geladen werden."); } });
    return () => { active = false; };
  }, [device.id, onError]);

  const hasPendingRequest = Boolean(state?.extensions.some(extensionRequestPending));

  useEffect(() => {
    if (!hasPendingRequest) return;
    let active = true;
    const timer = window.setInterval(() => {
      void getGlobalDeviceExtensions(device.id)
        .then((loaded) => { if (active) setState(loaded); })
        .catch(() => undefined);
    }, 3000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [device.id, hasPendingRequest]);

  async function toggle(extensionID: string, enabled: boolean, oneWay: boolean) {
    if (enabled && oneWay && !window.confirm(
      "Diese Extension führt einen nicht automatisch rückgängig machbaren Vorgang aus. Wirklich aktivieren?",
    )) return;
    setChanging(extensionID);
    try {
      const changed = await setGlobalDeviceExtension(device.id, extensionID, enabled);
      setState(changed);
      const extension = changed.extensions.find((item) => item.id === extensionID);
      onNotice(enabled && extension && extensionRequestPending(extension)
        ? `Installation von ${extension.name} ${extension.desired_version} wurde angefordert.`
        : enabled ? "Extension wurde aktiviert und an den Pi übertragen."
          : extension && extensionRequestPending(extension)
            ? `Das Stoppen von ${extension.name} wurde angefordert.`
            : "Extension wurde deaktiviert.");
    } catch (reason) {
      if (reason instanceof APIError && reason.status === 409) {
        onError("Für Extensions muss der Pi-Agent zuerst auf Version 0.5.0 oder neuer aktualisiert werden.");
      } else {
        onError("Die Extension konnte nicht geändert werden.");
      }
    } finally {
      setChanging("");
    }
  }

  async function update(extensionID: string) {
    setChanging(extensionID);
    try {
      const changed = await setGlobalDeviceExtension(device.id, extensionID, true);
      setState(changed);
      onNotice("Das Extension-Update wurde angefordert und wird vom Pi signaturgeprüft installiert.");
    } catch {
      onError("Das Extension-Update konnte nicht angefordert werden.");
    } finally {
      setChanging("");
    }
  }

  return (
    <section className={`extension-panel${embedded ? " embedded-extension-panel" : ""}`} aria-label={`Extensions für ${deviceLabel(device)}`}>
      <div className="extension-heading">
        <div><h3>Extensions · {deviceLabel(device)}</h3><p>Jede Extension läuft als eigenes, eingeschränktes systemd-Programm auf dem Pi.</p></div>
        {!embedded && <button className="close-button" type="button" onClick={onClose}>Schließen</button>}
      </div>
      {!state && !loadFailed && <p className="subtle-loading">Extensions werden geladen …</p>}
      {loadFailed && <p className="attention-text" role="alert">Extensions konnten nicht geladen werden.</p>}
      {state?.extensions.length === 0 && <p className="device-detail-note">Für dieses Gerät sind derzeit keine Extensions freigegeben.</p>}
      {state?.extensions.map((extension) => (
        <div className="extension-row" key={extension.id}>
          <div className="extension-copy"><strong>{extension.name}</strong><p>{extension.description}</p>
            <small>Verfügbar: {extension.latest_version}{extension.installed_version ? ` · Installiert: ${extension.installed_version}` : " · Noch nicht installiert"}</small>
            {extensionRequestPending(extension) && <small className="extension-requested" role="status">{extension.enabled ? `${extension.installed_version ? "Update" : "Installation"} auf ${extension.desired_version} angefordert` : "Stoppen angefordert"} · Warte auf Bestätigung des Pi …</small>}
            {extension.state && <small>Status: {extension.state === "stopping" ? "wird gestoppt" : extension.state}{extension.error_code ? ` · ${extensionCodeTexts[extension.error_code] ?? extension.error_code}` : ""}</small>}
            {extension.status_message && <small className={extension.state === "error" || extension.error_code ? "attention-text" : ""}>{extension.status_message}</small>}
            {extension.one_way && <small>Die Wirkung dieser Extension wird durch Ausschalten nicht automatisch rückgängig gemacht.</small>}
            {extension.enabled && extension.update_available && extension.desired_version !== extension.latest_version && <button className="text-action" type="button" disabled={changing === extension.id} onClick={() => void update(extension.id)}>Update auf {extension.latest_version}</button>}
          </div>
          <label className="toggle-control">
            <input
              type="checkbox"
              aria-label={`${extension.name} ${extension.enabled ? "ausschalten" : "einschalten"}`}
              checked={extension.enabled}
              disabled={changing === extension.id}
              onChange={(event) => void toggle(extension.id, event.target.checked, extension.one_way)}
            />
            <span aria-hidden="true" />
            <b>{extension.enabled ? "Ein" : "Aus"}</b>
          </label>
        </div>
      ))}
    </section>
  );
}
